import React, { ReactElement, ReactNode, useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import './ProjectScopeBar.scss';

export interface ProjectScopeBarProps {
    scope: 'all' | 'selected';
    projectNames: string[];
}

const VISIBLE_NAME_COUNT = 3;
const LOCKED_TOOLTIP = 'Project selection has been preset by your administrator.';
const ALL_TOOLTIP = 'No project filter is applied. Inactive projects are excluded from scores.';

const ProjectName = ({ name }: { name: string }): ReactElement => (
    <strong className='project-scope-name' title={name}>{name}</strong>
);

const joinNames = (names: string[], tail?: ReactNode): ReactNode[] => {
    const parts: ReactNode[] = [];
    const items: ReactNode[] = [...names.map((name, i) => <ProjectName key={`${i}-${name}`} name={name} />), ...(tail ? [tail] : [])];
    items.forEach((item, i) => {
        if (i > 0) {
            if (items.length === 2) parts.push(' and ');
            else parts.push(i === items.length - 1 ? ', and ' : ', ');
        }
        parts.push(<React.Fragment key={`item-${i}`}>{item}</React.Fragment>);
    });
    return parts;
};

const ProjectScopeBar = ({ scope, projectNames }: ProjectScopeBarProps): ReactElement => {
    const overlayRef = useRef<OverlayPanel>(null);

    if (scope === 'all') {
        return (
            <div className='project-scope-bar' role='note'>
                <span
                    className='project-scope-icon project-scope-tooltip-all'
                    tabIndex={0}
                    role='img'
                    aria-label={ALL_TOOLTIP}>
                    <i className='fa-regular fa-circle-info' aria-hidden='true' />
                </span>
                <Tooltip target='.project-scope-tooltip-all' content={ALL_TOOLTIP} position='bottom' event='both' />
                <span className='project-scope-text'>This tab shows information from all active projects.</span>
            </div>
        );
    }

    if (projectNames.length === 0) {
        return (
            <div className='project-scope-bar' role='note'>
                <span className='project-scope-text'>
                    <em>There are no active projects available on this tab.</em>
                </span>
            </div>
        );
    }

    const shown = projectNames.slice(0, VISIBLE_NAME_COUNT);
    const remaining = projectNames.length - shown.length;
    const moreButton = remaining > 0 ? (
        <button
            type='button'
            className='project-scope-more'
            aria-haspopup='dialog'
            onClick={(e) => overlayRef.current?.toggle(e)}>
            {remaining} more
        </button>
    ) : undefined;

    return (
        <div className='project-scope-bar' role='note'>
            <span
                className='project-scope-icon project-scope-tooltip-locked'
                tabIndex={0}
                role='img'
                aria-label={LOCKED_TOOLTIP}>
                <i className='fa-regular fa-lock' aria-hidden='true' />
            </span>
            <Tooltip target='.project-scope-tooltip-locked' content={LOCKED_TOOLTIP} position='bottom' event='both' />
            <span className='project-scope-text'>
                {`This tab shows information from the following ${projectNames.length === 1 ? 'project' : 'projects'}: `}
                {joinNames(shown, moreButton)}
            </span>
            {moreButton && (
                <OverlayPanel ref={overlayRef} className='project-scope-popover' dismissable={true} closeOnEscape={true}>
                    <div className='project-scope-popover-title'>Projects on this tab ({projectNames.length})</div>
                    <ul className='project-scope-popover-list'>
                        {projectNames.map((name, i) => (
                            <li key={`${i}-${name}`} title={name}>{name}</li>
                        ))}
                    </ul>
                </OverlayPanel>
            )}
        </div>
    );
};

export default ProjectScopeBar;
